import { type FC, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import AaFeatureGenomeView from "./AaFeatureGenomeView.js";
import AaFeatureSummaryView from "./AaFeatureSummaryView.js";
import { isGenomeViewerEnabledEntity } from "@/app-utils/entity.ts";
import { type IAaFeatureState, loadAaFeature } from "@/redux/modules/aaFeature.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";

const AaFeatureDetailPage: FC = () => {
  // redux
  const dispatch = useDispatch<ReduxDispatch>();
  const { aaFeature, isLoading } = useSelector<IAppState, IAaFeatureState>((state) => state.aaFeature, shallowEqual);

  // router
  const { aaFeatureId } = useParams<"aaFeatureId">();

  useEffect(() => {
    void dispatch(loadAaFeature(Number(aaFeatureId)));
  }, [dispatch, aaFeatureId]);

  const getNavItems = useCallback((): IRouteNavItemProps[] => {
    const genomeViewerDisabled = !isGenomeViewerEnabledEntity(aaFeature?.genome);
    const genomeViewDec = {
      disabled: isLoading || genomeViewerDisabled,
      tooltip: genomeViewerDisabled ? "Only features on genomic sequences can be loaded to the Genome Viewer." : null,
    };

    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <AaFeatureSummaryView />,
        default: true,
      },
      {
        label: "Genome View",
        route: "genome",
        disabled: genomeViewDec.disabled,
        disabledTooltip: genomeViewDec.tooltip,
        pageComponent: <AaFeatureGenomeView />,
      },
    ];
  }, [isLoading, aaFeature?.id]);

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.AAFEATURE}
      groupSubtypeId={aaFeature?.aaFeatureType.id}
      entityId={Number(aaFeatureId)}
      getDisplayName={() => aaFeature?.name ?? ""}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export { AaFeatureDetailPage };
